// Removed legacy template initialization API (update-dealer-feed-config)

// Segment stats / inventory summary
// Dashboard inventory summary (CURRENT inventory snapshot from DynamoDB CurrentInventory)
export const getCurrentInventoryStats = async (
  endpoint: string,
  accountId: string,
): Promise<any> => {
  const path = `current-inventory-stats/${accountId}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: any = await response.json()

    if (!response.ok) {
      console.warn('ise', 'getCurrentInventoryStats', 'non-200 response', {
        status: response.status,
        statusText: response.statusText,
        body: responseJson,
      })
      // Safe fallback shape for Dashboard
      return {
        jazelId: accountId,
        segments: [],
        totalVehicles: 0,
        counts: { all: 0, new: 0, used: 0, cpo: 0 },
        sourceCounts: { feed: 0, manual: 0 },
        feedSourceCounts: {},
        sourceJazelIdCounts: {},
        websiteVisibilityCounts: { VISIBLE: 0, HIDDEN: 0 },
        draftCounts: { manualAdds: 0 },
        dealerHiddenCount: 0,
      }
    }

    return responseJson
  } catch (error) {
    console.error('ise', 'getCurrentInventoryStats', error)
    // Safe fallback shape for Dashboard
    return {
      jazelId: accountId,
      segments: [],
      totalVehicles: 0,
      counts: { all: 0, new: 0, used: 0, cpo: 0 },
      sourceCounts: { feed: 0, manual: 0 },
      feedSourceCounts: {},
      sourceJazelIdCounts: {},
      websiteVisibilityCounts: { VISIBLE: 0, HIDDEN: 0 },
      draftCounts: { manualAdds: 0 },
      dealerHiddenCount: 0,
    }
  }
}

export const getImportFeedJobHistory = async (
  endpoint: string,
  accountId: string,
  days = 7,
  limit = 20,
): Promise<any[]> => {
  const path = `current-inventory-stats/${accountId}?action=job-history&days=${days}&limit=${limit}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })

  try {
    const json: any = await response.json()
    if (!response.ok) {
      console.warn('ise', 'getImportFeedJobHistory', 'non-200', json)
      return []
    }
    return Array.isArray(json?.jobs) ? json.jobs : []
  } catch (error) {
    console.error('ise', 'getImportFeedJobHistory', error)
    return []
  }
}

export type InventoryCountSummary = {
  indexedVisible: number | null
  currentInventory?: {
    total?: number
    visibleOnWebsite?: number
    hiddenFromWebsite?: number
  }
}

// Inventory-count response:
// - indexedVisible: OpenSearch membership count (what shoppers see)
// - currentInventory: Dynamo operational website-visibility counts
export const getInventoryCountSummary = async (
  endpoint: string,
  accountId: string,
): Promise<InventoryCountSummary> => {
  const params = new URLSearchParams({ jazelId: accountId })
  const response = await fetch(`${endpoint}/inventory-count?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const json: any = await response.json()
    if (!response.ok) {
      return { indexedVisible: null }
    }

    const indexedVisible = json?.openSearch?.indexedVisible
    const current = json?.currentInventory

    return {
      indexedVisible: typeof indexedVisible === 'number' ? indexedVisible : null,
      currentInventory:
        current && typeof current === 'object'
          ? {
              total: typeof current.total === 'number' ? current.total : undefined,
              visibleOnWebsite:
                typeof current.visibleOnWebsite === 'number'
                  ? current.visibleOnWebsite
                  : undefined,
              hiddenFromWebsite:
                typeof current.hiddenFromWebsite === 'number'
                  ? current.hiddenFromWebsite
                  : undefined,
            }
          : undefined,
    }
  } catch (e) {
    console.error('ise', 'getInventoryCountSummary', e)
    return { indexedVisible: null }
  }
}

export type InventoryActivityCounts = {
  added: number
  removed: number
  truncated: boolean
  vinsTruncated?: boolean
  sample?: {
    addedVins: string[]
    removedVins: string[]
  }
  vins?: {
    added: string[]
    removed: string[]
  }
}

export const getInventoryActivityCounts = async (params: {
  endpoint: string
  accountId: string
  startIso: string
  endIso: string
  includeVins?: boolean
  maxVins?: number
}): Promise<InventoryActivityCounts | null> => {
  const qs = new URLSearchParams()
  qs.set('action', 'activity')
  qs.set('jazelId', params.accountId)
  qs.set('start', params.startIso)
  qs.set('end', params.endIso)
  if (params.includeVins) qs.set('includeVins', '1')
  if (typeof params.maxVins === 'number') qs.set('maxVins', String(params.maxVins))

  const response = await fetch(`${params.endpoint}/inventory?${qs.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })

  try {
    const json: any = await response.json()
    if (!response.ok) return null
    const counts = json?.counts
    if (!counts || typeof counts !== 'object') return null
    return {
      added: typeof counts.added === 'number' ? counts.added : 0,
      removed: typeof counts.removed === 'number' ? counts.removed : 0,
      truncated: !!counts.truncated,
      vinsTruncated: !!counts.vinsTruncated,
      sample: counts.sample,
      vins: counts.vins,
    }
  } catch (e) {
    console.error('ise', 'getInventoryActivityCounts', e)
    return null
  }
}

export const getDealerSources = async (
  endpoint: string,
  accountId: string,
): Promise<Array<{ id: string; friendlyName?: string; type: string }>> => {
  const path = `dealer-feeds/${accountId}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: IGetDealerSourcesResponse = await response.json()

    if (!response.ok) {
      return []
    }

    return responseJson.feeds
  } catch (error) {
    console.error('ise', 'getDealerSources', error)
    return []
  }
}

export const getA5DebugStats = async (
  endpoint: string,
  accountId: string,
  params: any
): Promise<any> => {
    const queryParams = new URLSearchParams()
    if (params.a5Id) queryParams.append('a5Id', String(params.a5Id))
    if (Array.isArray(params.a5Ids) && params.a5Ids.length) {
        queryParams.append('a5Ids', params.a5Ids.join(','))
    }
    if (params.condition) queryParams.append('condition', params.condition)
    if (params.sample) queryParams.append('sample', String(params.sample))
    if (params.page) queryParams.append('page', String(params.page))
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize))
    if (params.diff) queryParams.append('diff', String(params.diff))
    if (Array.isArray(params.makes) && params.makes.length) {
        queryParams.append('makes', params.makes.join(','))
    }
    if (params.primaryFeed) queryParams.append('primaryFeed', String(params.primaryFeed))
    
    // endpoint is /a5-debug/{jazelId}
    const path = `a5-debug/${accountId}`
    
    const response = await fetch(`${endpoint}/${path}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    })
    
    if (!response.ok) {
        const errorBody: any = await response.json().catch(() => ({}))
        const baseMsg = errorBody.error || `Request failed with status ${response.status}`
        const details = errorBody.details ? `: ${String(errorBody.details)}` : ''
        throw new Error(`${baseMsg}${details}`)
    }
    
    return response.json()
}

export const getA5VinComparison = async (
  endpoint: string,
  accountId: string,
  vin: string,
  params: { a5Id?: number; a5Ids?: number[] }
): Promise<any> => {
  const queryParams = new URLSearchParams()
  if (params.a5Id) queryParams.append('a5Id', String(params.a5Id))
  if (Array.isArray(params.a5Ids) && params.a5Ids.length) {
    queryParams.append('a5Ids', params.a5Ids.join(','))
  }

  const path = `a5-debug/${accountId}/vin/${encodeURIComponent(vin)}`

  const response = await fetch(`${endpoint}/${path}?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const errorBody: any = await response.json().catch(() => ({}))
    const baseMsg = errorBody.error || `Request failed with status ${response.status}`
    const details = errorBody.details ? `: ${String(errorBody.details)}` : ''
    throw new Error(`${baseMsg}${details}`)
  }

  return response.json()
}

export const getDebugReport = async (
  endpoint: string,
  accountId: string,
  params: { include?: string[]; sample?: number; primaryFeed?: string | null }
): Promise<any> => {
  const queryParams = new URLSearchParams()
  if (params.include && params.include.length > 0) {
    queryParams.append('include', params.include.join(','))
  }
  if (typeof params.sample === 'number') {
    queryParams.append('sample', String(params.sample))
  }
  if (params.primaryFeed) {
    queryParams.append('primaryFeed', String(params.primaryFeed))
  }

  // endpoint is /debug/{jazelId}
  const path = `debug/${accountId}`

  const response = await fetch(`${endpoint}/${path}?${queryParams.toString()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    throw new Error(errorBody.error || `Request failed with status ${response.status}`)
  }

  return response.json()
}
